import { useState } from 'react';
import { createDefaultColorRule } from '../../../../utils/colorRuleEngine';
import { FieldConfigurator } from '../../../field-configurator/FieldConfigurator';
import {
	ColumnConfiguration,
	FieldConfiguration,
	ListBuilderConfiguration,
} from '../../../types';
import { FieldsSidebar } from './FieldsSidebar';

interface FieldsTabProps {
	selectedColumn: ColumnConfiguration | null;
	configuration?: ListBuilderConfiguration;
	onUpdateColumn: (updates: Partial<ColumnConfiguration>) => void;
	isModalOpen?: boolean;
	allColumns?: ColumnConfiguration[];
	onUpdateAllColumns?: (columns: ColumnConfiguration[]) => void;
	// On read-only lists (built-in defaults, non-editable saved searches) the
	// sidebar still lets you click a field to inspect its configuration, but
	// the configurator pane renders disabled so it can't be edited.
	isReadOnly?: boolean;
}

export function FieldsTab({
	selectedColumn,
	configuration,
	onUpdateColumn,
	isModalOpen = false,
	allColumns,
	onUpdateAllColumns,
	isReadOnly = false,
}: FieldsTabProps) {
	const [selectedFieldIndex, setSelectedFieldIndex] = useState<number | null>(
		null,
	);

	if (!selectedColumn) {
		return null;
	}

	const fields = selectedColumn.fields || [];
	const selectedField =
		selectedFieldIndex !== null &&
		fields[selectedFieldIndex] &&
		'displayStyle' in fields[selectedFieldIndex]
			? (fields[selectedFieldIndex] as FieldConfiguration)
			: null;

	// Get available fields from the Objects tab configuration
	const getAvailableFieldsFromObjects = () => {
		if (!configuration?.objects || configuration.objects.length === 0) {
			return [];
		}

		const availableFields: Array<{
			fieldName: string;
			fieldLabel: string;
			objectTable: string;
			objectLabel: string;
		}> = [];

		configuration.objects.forEach((obj) => {
			obj.selectedFields.forEach((field) => {
				availableFields.push({
					fieldName: field.fieldName,
					fieldLabel: field.fieldLabel,
					objectTable: obj.objectTable,
					objectLabel: obj.objectLabel,
				});
			});
		});

		return availableFields;
	};

	const availableFieldsFromObjects = getAvailableFieldsFromObjects();

	const addField = () => {
		const firstField = availableFieldsFromObjects[0];
		// First field in a column uses default (textPrimary), subsequent fields use textSecondary
		// This matches the pattern used by all default list configurations
		const hasExistingFields = fields.length > 0;
		const colorRule = hasExistingFields
			? { type: 'theme' as const, themeColor: 'textSecondary' }
			: createDefaultColorRule();
		const newField: FieldConfiguration = {
			id: `field_${Date.now()}`,
			displayStyle: 'text',
			dataConfig: {
				type: 'field',
				field: firstField
					? {
							objectTable: firstField.objectTable,
							fieldName: firstField.fieldName,
						}
					: undefined,
				// `defaultValue` omitted — '-' is the framework-level default
				// (see FieldRenderer.getFieldValue). Set explicitly to '' or
				// null here only if you want a blank cell instead.
			},
			styleConfig: {
				size: 'sm',
				colorRule,
			},
		};

		const updatedFields = [...fields, newField];
		onUpdateColumn({ fields: updatedFields });
		setSelectedFieldIndex(updatedFields.length - 1);
	};

	const updateField = (
		fieldIndex: number,
		updates: Partial<FieldConfiguration>,
	) => {
		const updatedFields = [...fields];
		updatedFields[fieldIndex] = { ...updatedFields[fieldIndex], ...updates };
		onUpdateColumn({ fields: updatedFields });
	};

	const deleteField = (fieldIndex: number) => {
		const updatedFields = fields.filter((_, i) => i !== fieldIndex);
		onUpdateColumn({ fields: updatedFields });

		// Adjust selected index if needed
		if (selectedFieldIndex !== null) {
			if (selectedFieldIndex === fieldIndex) {
				setSelectedFieldIndex(null);
			} else if (selectedFieldIndex > fieldIndex) {
				setSelectedFieldIndex(selectedFieldIndex - 1);
			}
		}
	};

	const reorderFields = (reorderedFields: FieldConfiguration[]) => {
		// Get the ID of currently selected field before reordering
		const selectedFieldId =
			selectedFieldIndex !== null ? fields[selectedFieldIndex]?.id : null;

		onUpdateColumn({ fields: reorderedFields });

		// Find new index of the previously selected field
		if (selectedFieldId) {
			const newIndex = reorderedFields.findIndex(
				(field) => field.id === selectedFieldId,
			);
			if (newIndex !== -1 && newIndex !== selectedFieldIndex) {
				setSelectedFieldIndex(newIndex);
			}
		}
	};

	const receiveField = (field: FieldConfiguration, targetIndex: number) => {
		// Add the field to this column at the target position
		const updatedFields = [...fields];
		updatedFields.splice(targetIndex, 0, field);
		onUpdateColumn({ fields: updatedFields });

		// Remove the field from its original column
		if (allColumns && onUpdateAllColumns) {
			const updatedColumns = allColumns.map((col) => {
				if (col.id === selectedColumn.id) {
					// This is the current column, already updated above
					return { ...col, fields: updatedFields };
				} else {
					// Check if this column has the field and remove it
					const fieldIndex = col.fields.findIndex((f) => f.id === field.id);
					if (fieldIndex !== -1) {
						const newFields = [...col.fields];
						newFields.splice(fieldIndex, 1);
						return { ...col, fields: newFields };
					}
				}
				return col;
			});
			onUpdateAllColumns(updatedColumns);
		}
	};

	return (
		<div className="flex flex-1 h-full">
			<FieldsSidebar
				fields={fields}
				selectedFieldIndex={selectedFieldIndex}
				onSelectField={setSelectedFieldIndex}
				onAddField={addField}
				onDeleteField={deleteField}
				onReorderFields={reorderFields}
				onReceiveField={receiveField}
				columnId={selectedColumn.id}
				availableFields={availableFieldsFromObjects}
				isReadOnly={isReadOnly}
			/>

			<div className="flex-1 h-full overflow-hidden">
				<FieldConfigurator
					field={selectedField}
					fieldIndex={selectedFieldIndex || 0}
					configuration={configuration}
					onUpdateField={(updates) => {
						if (selectedFieldIndex !== null) {
							updateField(selectedFieldIndex, updates);
						}
					}}
					isModalOpen={isModalOpen}
					isReadOnly={isReadOnly}
				/>
			</div>
		</div>
	);
}
